import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../utils/api';
import './LoginPage.css';

const LoginPage = () => {
  const [activeTab, setActiveTab] = useState('login'); // 'login', 'signup', 'waitlist'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'teacher',
    organization: '',
    phone: '',
    interests: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        interests: checked 
          ? [...prev.interests, value]
          : prev.interests.filter(interest => interest !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password validation for login and signup
    if (activeTab !== 'waitlist') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }

    // Confirm password for signup
    if (activeTab === 'signup') {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Name validation for signup and waitlist
    if (activeTab !== 'login' && !formData.name) {
      newErrors.name = 'Name is required';
    }

    // Organization validation for waitlist
    if (activeTab === 'waitlist' && !formData.organization) {
      newErrors.organization = 'Organization is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (activeTab === 'login') {
        // Handle login
        const response = await apiService.login(formData.email, formData.password);
        
        if (response.success) {
          // Store auth token
          apiService.setAuthToken(response.token);
          
          // Store user data (optional)
          localStorage.setItem('user', JSON.stringify(response.user));
          
          alert(`Welcome back, ${response.user.name}!`);
          navigate('/demo');
        }
      } else if (activeTab === 'signup') {
        // Handle signup
        const response = await apiService.signup({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          organization: formData.organization,
          phone: formData.phone
        });
        
        if (response.success) {
          alert('Account created successfully! Please check your email to verify your account.');
          setActiveTab('login');
          // Clear form
          setFormData({
            email: formData.email, // Keep email for convenience
            password: '',
            confirmPassword: '',
            name: '',
            role: 'teacher',
            organization: '',
            phone: '',
            interests: []
          });
        }
      } else if (activeTab === 'waitlist') {
        // Handle waitlist signup
        const response = await apiService.addToWaitlist({
          name: formData.name,
          email: formData.email,
          role: formData.role,
          organization: formData.organization,
          phone: formData.phone,
          interests: formData.interests
        });
        
        if (response.success) {
          alert(`Thank you for joining our waitlist! You're #${response.waitlistPosition} in line. We'll be in touch soon.`);
          // Clear form
          setFormData({
            email: '',
            password: '',
            confirmPassword: '',
            name: '',
            role: 'teacher',
            organization: '',
            phone: '',
            interests: []
          });
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
      
      // Show user-friendly error messages
      if (error.message.includes('User already exists')) {
        alert('An account with this email already exists. Please try logging in instead.');
      } else if (error.message.includes('Invalid email or password')) {
        alert('Invalid email or password. Please check your credentials and try again.');
      } else if (error.message.includes('Email already exists in our waitlist')) {
        alert('This email is already on our waitlist. We\'ll be in touch soon!');
      } else if (error.message.includes('Server error')) {
        alert('We\'re having technical difficulties. Please try again in a few minutes.');
      } else {
        alert('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <header>
        <nav className="container">
          <Link to="/" className="logo">ENRY</Link>
          <ul className="nav-links">
            <li><Link to="/demo">Demo</Link></li>
            <li><Link to="/#features">Features</Link></li>
            <li><Link to="/#partnerships">Partners</Link></li>
            <li><Link to="/login" className="active">Login</Link></li>
          </ul>
        </nav>
      </header>

      <section className="auth-section">
        <div className="container">
          <div className="auth-container">
            <div className="auth-header">
              <h1>Welcome to ENRY</h1>
              <p>Join educators worldwide in revolutionizing student tracking</p>
            </div>

            <div className="tab-selector">
              <button 
                className={`tab-button ${activeTab === 'login' ? 'active' : ''}`}
                onClick={() => setActiveTab('login')}
              >
                Sign In
              </button>
              <button 
                className={`tab-button ${activeTab === 'signup' ? 'active' : ''}`}
                onClick={() => setActiveTab('signup')}
              >
                Sign Up
              </button>
              <button 
                className={`tab-button ${activeTab === 'waitlist' ? 'active' : ''}`}
                onClick={() => setActiveTab('waitlist')}
              >
                Join Waitlist
              </button>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {activeTab !== 'login' && (
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? 'error' : ''}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {activeTab !== 'waitlist' && (
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={errors.password ? 'error' : ''}
                    placeholder="Enter your password"
                  />
                  {errors.password && <span className="error-message">{errors.password}</span>}
                </div>
              )}

              {activeTab === 'signup' && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={errors.confirmPassword ? 'error' : ''}
                    placeholder="Confirm your password"
                  />
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              )}

              {(activeTab === 'signup' || activeTab === 'waitlist') && (
                <div className="form-group">
                  <label htmlFor="role">Your Role</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                  >
                    <option value="teacher">Teacher</option>
                    <option value="administrator">Administrator</option>
                    <option value="counselor">School Counselor</option>
                    <option value="coach">Academic Coach</option>
                    <option value="principal">Principal</option>
                    <option value="district">District Administrator</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}

              {activeTab === 'waitlist' && (
                <>
                  <div className="form-group">
                    <label htmlFor="organization">School/Organization</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className={errors.organization ? 'error' : ''}
                      placeholder="Enter your school or organization name"
                    />
                    {errors.organization && <span className="error-message">{errors.organization}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number (Optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Areas of Interest (Select all that apply)</label>
                    <div className="checkbox-group">
                      {[
                        'Student Progress Tracking',
                        'Intervention Management',
                        'MTSS Implementation',
                        'Data Analytics',
                        'Parent Communication',
                        'Compliance Reporting'
                      ].map(interest => (
                        <label key={interest} className="checkbox-label">
                          <input
                            type="checkbox"
                            value={interest}
                            checked={formData.interests.includes(interest)}
                            onChange={handleInputChange}
                          />
                          <span className="checkbox-text">{interest}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 
                 activeTab === 'login' ? 'Sign In' :
                 activeTab === 'signup' ? 'Create Account' : 'Join Waitlist'}
              </button>

              {activeTab === 'login' && (
                <div className="auth-links">
                  <a href="#forgot-password" className="forgot-password">
                    Forgot your password?
                  </a>
                </div>
              )}
            </form>

            <div className="auth-footer">
              {activeTab === 'login' ? (
                <p>
                  Don't have an account? 
                  <button 
                    className="link-button" 
                    onClick={() => setActiveTab('signup')}
                  >
                    Sign up here
                  </button>
                </p>
              ) : (
                <p>
                  Already have an account? 
                  <button 
                    className="link-button" 
                    onClick={() => setActiveTab('login')}
                  >
                    Sign in here
                  </button>
                </p>
              )}
              
              <p className="waitlist-cta">
                Want early access? 
                <button 
                  className="link-button" 
                  onClick={() => setActiveTab('waitlist')}
                >
                  Join our waitlist
                </button>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginPage; 